import React, { useEffect } from 'react';

const MediatorPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Mediator Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Mediator Pattern - define how objects interact through a central mediator. Learn with chat room and air traffic control examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Mediator Pattern - define how objects interact through a central mediator. Learn with chat room and air traffic control examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'mediator pattern, design patterns, behavioral patterns, object interaction, decoupling, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'mediator pattern, design patterns, behavioral patterns, object interaction, decoupling, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Mediator Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Define how objects interact through a mediator
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Mediator pattern defines how a set of objects interact with each other. Instead of 
            objects communicating directly, they communicate through a central mediator object. 
            This promotes loose coupling by preventing objects from referring to each other explicitly.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            When objects interact directly with many other objects, the system becomes tightly 
            coupled and hard to maintain. Changes to one object require changes to many others. 
            The Mediator pattern centralizes complex communications and control logic.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Chat Room</h2>
          <div style={styles.codeBlock}>
{`// Mediator interface
interface ChatMediator {
    void sendMessage(String message, User user);
    void addUser(User user);
    void removeUser(User user);
}

// Colleague interface
abstract class User {
    protected ChatMediator mediator;
    protected String name;
    
    public User(ChatMediator mediator, String name) {
        this.mediator = mediator;
        this.name = name;
    }
    
    public abstract void send(String message);
    public abstract void receive(String message, String from);
    
    public String getName() { return name; }
}

// Concrete mediator
class ChatRoom implements ChatMediator {
    private List<User> users;
    private String roomName;
    
    public ChatRoom(String roomName) {
        this.roomName = roomName;
        this.users = new ArrayList<>();
    }
    
    @Override
    public void addUser(User user) {
        users.add(user);
        System.out.println(user.getName() + " joined the chat room: " + roomName);
        // Notify other users
        for (User u : users) {
            if (u != user) {
                u.receive(user.getName() + " joined the chat", "System");
            }
        }
    }
    
    @Override
    public void removeUser(User user) {
        users.remove(user);
        System.out.println(user.getName() + " left the chat room");
        // Notify other users
        for (User u : users) {
            u.receive(user.getName() + " left the chat", "System");
        }
    }
    
    @Override
    public void sendMessage(String message, User sender) {
        System.out.println("[" + roomName + "] " + sender.getName() + ": " + message);
        
        // Send to all users except sender
        for (User user : users) {
            if (user != sender) {
                user.receive(message, sender.getName());
            }
        }
    }
    
    public void sendPrivateMessage(String message, User sender, String recipientName) {
        User recipient = users.stream()
            .filter(u -> u.getName().equals(recipientName))
            .findFirst()
            .orElse(null);
            
        if (recipient != null) {
            System.out.println("[Private] " + sender.getName() + " to " + recipientName + ": " + message);
            recipient.receive("(Private) " + message, sender.getName());
        } else {
            sender.receive("User " + recipientName + " not found", "System");
        }
    }
    
    public String getRoomName() { return roomName; }
    public int getUserCount() { return users.size(); }
}

// Concrete colleagues
class RegularUser extends User {
    public RegularUser(ChatMediator mediator, String name) {
        super(mediator, name);
    }
    
    @Override
    public void send(String message) {
        System.out.println(name + " is typing...");
        mediator.sendMessage(message, this);
    }
    
    @Override
    public void receive(String message, String from) {
        System.out.println(name + " received: [" + from + "] " + message);
    }
}

class ModeratorUser extends User {
    public ModeratorUser(ChatMediator mediator, String name) {
        super(mediator, name);
    }
    
    @Override
    public void send(String message) {
        System.out.println("[MODERATOR] " + name + " is sending message...");
        mediator.sendMessage("[MOD] " + message, this);
    }
    
    @Override
    public void receive(String message, String from) {
        System.out.println("[MODERATOR] " + name + " received: [" + from + "] " + message);
    }
    
    public void kickUser(String username) {
        System.out.println("[MODERATOR] " + name + " is kicking user: " + username);
        // In real implementation, would remove user from mediator
    }
    
    public void muteUser(String username) {
        System.out.println("[MODERATOR] " + name + " muted user: " + username);
        // In real implementation, would prevent user from sending messages
    }
}

// Usage
public class MediatorDemo {
    public static void main(String[] args) {
        // Create chat room (mediator)
        ChatRoom generalChat = new ChatRoom("General Discussion");
        
        // Create users
        User alice = new RegularUser(generalChat, "Alice");
        User bob = new RegularUser(generalChat, "Bob");
        User charlie = new RegularUser(generalChat, "Charlie");
        User moderator = new ModeratorUser(generalChat, "AdminMike");
        
        // Add users to chat room
        generalChat.addUser(alice);
        generalChat.addUser(bob);
        generalChat.addUser(charlie);
        generalChat.addUser(moderator);
        
        System.out.println("\\n--- Chat Session Started ---\\n");
        
        // Users communicate through mediator
        alice.send("Hello everyone!");
        bob.send("Hi Alice! How's everyone doing?");
        charlie.send("Great to see you all here!");
        
        moderator.send("Welcome to the General Discussion room!");
        
        System.out.println("\\n--- Private Message ---\\n");
        
        // Private message (if implemented)
        // generalChat.sendPrivateMessage("Can you help me with something?", alice, "Bob");
        
        System.out.println("\\n--- Moderator Actions ---\\n");
        
        // Moderator actions
        if (moderator instanceof ModeratorUser) {
            ModeratorUser mod = (ModeratorUser) moderator;
            mod.muteUser("Charlie");
            mod.kickUser("Bob");
        }
        
        System.out.println("\\n--- User Leaving ---\\n");
        
        // User leaves
        generalChat.removeUser(bob);
        
        alice.send("Where did Bob go?");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Loose Coupling:</strong> Objects don't reference each other directly</li>
            <li style={styles.listItem}><strong>Centralized Control:</strong> Complex interaction logic is centralized</li>
            <li style={styles.listItem}><strong>Reusability:</strong> Individual objects can be reused in different contexts</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Mediator handles communication, objects handle their own logic</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When objects communicate in complex but well-defined ways</li>
            <li style={styles.listItem}>When you want to reduce coupling between communicating objects</li>
            <li style={styles.listItem}>When you need to centralize complex communications</li>
            <li style={styles.listItem}>When you want to reuse objects in different contexts</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Mediator pattern is excellent for managing complex object interactions. It's commonly 
            used in GUI frameworks, chat systems, and workflow engines where multiple objects need 
            to communicate but should remain loosely coupled.
          </p>
        </section>
      </main>
    </article>
  );
};

export default MediatorPattern;
